import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'
import classnames from 'classnames'

// Components
import { Button, Format, If, Text, Unless } from '../../../../..'
import ReadableTerm from './ReadableTerm'

// Styles
import styles from './Loan.module.scss'

const propTypes = {
  confirmEstimatedRate: PropTypes.func,
  editLoan: PropTypes.func,
  editBalance: PropTypes.func,
  interestRate: PropTypes.number,
  isHeloc: PropTypes.bool,
  needsConfirmed: PropTypes.bool,
  adjustable: PropTypes.bool,
  amount: PropTypes.number,
  armYearsInitial: PropTypes.object,
  date: PropTypes.string,
  lender: PropTypes.string,
  loanType: PropTypes.string,
  refi: PropTypes.bool,
  termYears: PropTypes.number,
  termYearsEstimated: PropTypes.number,
  principalLeft: PropTypes.number,
  isCurrent: PropTypes.bool,
}

const Note = props => (
  <Text
    element='div'
    className={styles.note}
    variant='neutralCool'
    variantCount='900'
    {...props}
  />
)

const Loan = ({
  confirmEstimatedRate,
  editLoan,
  editBalance,
  interestRate,
  isHeloc,
  needsConfirmed,
  adjustable,
  amount,
  armYearsInitial,
  date,
  lender,
  loanType,
  refi,
  termYears,
  termYearsEstimated,
  principalLeft,
  isCurrent,
}) => {
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!needsConfirmed) setSaving(false)
  }, [needsConfirmed])

  return (
    <div className={styles.loan}>
      <div
        className={classnames(
          styles.loanContainer,
          !isHeloc ? styles.withBalance : null
        )}
      >
        <Text
          element='h3'
          weight='light'
          size='m'
          className='title'
          variant='neutralCool'
          variantCount='900'
        >
          <Format.Currency value={amount} />
          {' - '}
          <Format.Date value={date} format='MMMM YYYY' />
        </Text>
        <Text variant='neutralCool' variantCount='700' size='xs'>
          <ReadableTerm
            adjustable={adjustable}
            armYearsInitial={armYearsInitial}
            loanType={loanType}
            termYears={termYears}
            termYearsEstimated={termYearsEstimated}
          />{' '}
          <FormattedMessage
            id='Mortgages.loans.rate'
            defaultMessage='at {interestRate}'
            values={{ interestRate: <Format.Rate value={interestRate} /> }}
          />
        </Text>

        <If condition={refi || lender}>
          <Text variant='neutralCool' variantCount='700' size='xs'>
            <If condition={refi}>
              <FormattedMessage
                id='Mortgages.loans.refi'
                defaultMessage='Refi'
              />
            </If>{' '}
            <If condition={lender}>
              <FormattedMessage
                id='Mortgages.loans.lender'
                defaultMessage='from {lender}'
                values={{ lender }}
              />
            </If>
          </Text>
        </If>

        <If condition={needsConfirmed || isHeloc}>
          <div className={styles.notes}>
            <If condition={needsConfirmed}>
              <Note>
                <FormattedMessage
                  id='Mortgages.loans.verify'
                  defaultMessage='Are these numbers correct?'
                />
              </Note>
              <Note>
                <Button.Group className={styles.buttons}>
                  <Button
                    compact
                    loading={saving}
                    onClick={() => {
                      setSaving(true)
                      confirmEstimatedRate()
                    }}
                  >
                    <FormattedMessage
                      id='Mortgages.loans.correct'
                      defaultMessage='Yes'
                    />
                  </Button>
                  <Button compact disabled={saving} onClick={editLoan}>
                    <FormattedMessage
                      id='Mortgages.loans.incorrect'
                      defaultMessage='No'
                    />
                  </Button>
                </Button.Group>
              </Note>
            </If>
            <If condition={isHeloc}>
              <Note>
                <FormattedMessage
                  id='Mortgages.disclaimer.heloc'
                  defaultMessage="Note: HELOC's are not used in calculating your monthly report yet."
                />
              </Note>
            </If>
          </div>
        </If>
        <Unless condition={needsConfirmed}>
          <Button
            minimal
            className={styles.button}
            onClick={editLoan}
            data-qa='edit-loan-details-button'
          >
            <FormattedMessage
              id='Mortgages.loans.edit'
              defaultMessage='Edit loan details'
            />
          </Button>
        </Unless>
      </div>
      <If condition={!isHeloc}>
        <div className={classnames(styles.loanContainer, styles.balance)}>
          <If condition={isCurrent}>
            <Text variant='neutralCool' variantCount='700' size='xs'>
              <FormattedMessage
                id='Mortgages.loans.currentBalance'
                defaultMessage='Current Balance: {balance}'
                values={{
                  balance: (
                    <Format.Currency value={principalLeft} format='0,0' />
                  ),
                }}
              />
            </Text>
            <Button minimal className={styles.button} onClick={editBalance}>
              <FormattedMessage
                id='Mortgages.loans.updateBalance'
                defaultMessage='Update balance'
              />
            </Button>
          </If>
        </div>
      </If>
    </div>
  )
}

Loan.propTypes = propTypes

export default Loan
